import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, RefreshCw, Share2, X, QrCode } from 'lucide-react';
import { salesGet, salesPost } from '@/lib/salesTeam';
import { useDashboardIdentity } from '@/hooks/useDashboardIdentity';
import {
  recruitApplyUrl, recruitLinkFor, recruitLinkRequest, type RecruitLinkRow,
} from '@/lib/partnerDirectory';
import styles from './PartnerDirectory.module.css';

/**
 * The live recruit links (GET /admin/sales-flow/campaigns). The route is
 * Super Admin only, the same check that issues a link, so the list shows the
 * card's button only once this read succeeds: no button that would refuse.
 */
export function useRecruitLinks() {
  const identity = useDashboardIdentity();
  return useQuery({
    queryKey: ['partner-recruit-links', identity],
    queryFn: () => salesGet<RecruitLinkRow[]>('admin/sales-flow/campaigns'),
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * "Recruit a Sales Partner" (Warren, 24 October 2026): a link and QR code a
 * prospect opens or scans to apply on evinitiative.com/partners. The link
 * carries a referral code (the existing attribution: the website sends it as
 * ref_code and the Api resolves it), so the application lands in Referral
 * applications credited to EVI's house book, or to the rep chosen here. EVI
 * still reviews and approves every application; approving sends the invite.
 * Same qrcode, clipboard and native share pattern as SalesInviteCard.
 */
export function RecruitPartnerCard({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const identity = useDashboardIdentity();
  const links = useRecruitLinks();
  const reps = useQuery({
    queryKey: ['sales-attribution-options'],
    queryFn: () => salesGet<Array<{ id: string; name: string }>>('admin/sales-staff/attribution-options'),
    enabled: links.isSuccess,
  });
  const [credit, setCredit] = useState('house');
  const [qr, setQr] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const link = links.data ? recruitLinkFor(links.data, credit) : null;
  const url = link ? recruitApplyUrl(link) : '';

  useEffect(() => {
    let cancelled = false;
    setQr('');
    if (url) {
      void import('qrcode')
        .then((m) => m.default.toDataURL(url, { width: 480, margin: 1 }))
        .then((value) => { if (!cancelled) setQr(value); })
        .catch(() => { if (!cancelled) setMessage('QR code unavailable. You can still copy the link.'); });
    }
    return () => { cancelled = true; };
  }, [url]);

  async function issue(rotate: boolean) {
    setBusy(true);
    setMessage('');
    try {
      await salesPost('admin/sales-flow/campaigns', recruitLinkRequest(credit, rotate));
      await client.invalidateQueries({ queryKey: ['partner-recruit-links', identity] });
      setMessage(rotate ? 'New link ready. The old link now applies to the EVI house book.' : 'Link ready.');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Could not create the link.');
    } finally {
      setBusy(false);
    }
  }

  const copy = () => void navigator.clipboard.writeText(url)
    .then(() => setMessage('Link copied.'))
    .catch(() => setMessage('Select and copy the link above.'));
  const share = () => {
    if (navigator.share) void navigator.share({ title: 'Apply to the EV Initiative Sales Partner Program', url }).catch(() => undefined);
    else copy();
  };
  const repName = credit === 'house' ? null : reps.data?.find((r) => r.id === credit)?.name ?? null;

  return (
    <section className={styles.recruit} aria-labelledby="recruit-partner-title">
      <div className={styles.recruitMain}>
        <div className={styles.recruitTitleRow}>
          <h2 id="recruit-partner-title" className={styles.recruitTitle}>Recruit a Sales Partner</h2>
          <button type="button" className={styles.iconClose} onClick={onClose} aria-label="Close recruit card"><X size={14} /></button>
        </div>
        <p className={styles.recruitBody}>
          Share this link or QR code with an electrician, installer or distributor. They apply on
          evinitiative.com, the application arrives in Referral applications, and EVI reviews it within
          two business days. Approving it sends their Sales Partner invite.
        </p>
        <label className={styles.field}>
          Credit applications to
          <select className={styles.select} value={credit} onChange={(e) => { setCredit(e.target.value); setMessage(''); }}>
            <option value="house">EVI house book (no rep)</option>
            {(reps.data ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
        {links.error && <p className={styles.error} role="alert">{links.error.message}</p>}
        {link ? (
          <>
            <label className={styles.field}>
              Application link
              <input className={`${styles.input} ${styles.linkInput}`} readOnly value={url} aria-label="Sales Partner application link"
                onFocus={(e) => e.currentTarget.select()} />
            </label>
            <span className={styles.code}>
              Referral code {link.id}{repName ? `, credited to ${repName}` : ', EVI house book'}
            </span>
            <div className={styles.actions}>
              <button className={styles.secondaryBtn} type="button" onClick={copy}><Copy size={13} /> Copy link</button>
              <button className={styles.secondaryBtn} type="button" onClick={share}><Share2 size={13} /> Share</button>
              {qr && <a className={styles.secondaryBtn} href={qr} download="evi-sales-partner-application-qr.png"><Download size={13} /> Download QR</a>}
              <button className={styles.secondaryBtn} type="button" disabled={busy}
                onClick={() => { if (window.confirm('Replace this link? Applications from the old link go to the EVI house book.')) void issue(true); }}>
                <RefreshCw size={13} /> New link
              </button>
            </div>
          </>
        ) : (
          <div className={styles.actions}>
            <button className={styles.secondaryBtn} type="button" disabled={busy || links.isLoading} onClick={() => void issue(false)}>
              <QrCode size={13} /> Create link
            </button>
          </div>
        )}
        {message && <p className={styles.status} role="status">{message}</p>}
      </div>
      <div className={styles.qrWrap}>
        {qr
          ? <img className={styles.qr} src={qr} width={180} height={180} alt="QR code for the Sales Partner application link" />
          : <div className={styles.qrPlaceholder}>{link ? 'Preparing QR code' : 'Create a link to show its QR code'}</div>}
      </div>
    </section>
  );
}
